import { useState, FormEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import Select from "react-select";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import ValidationMessage from "src/helpers/ValidationMessage";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import moment from "moment";
import DatePicker from "react-datepicker";
import { LanguageDate } from "src/helpers/CheckLanguageDate";

interface StoreDiscountProps {
  onShowModal: boolean;
  setShowModal: (visible: boolean) => void;
  setModified: (visible: boolean) => void;
}

const StoreDiscount: React.FC<StoreDiscountProps> = ({
  onShowModal,
  setShowModal,
  setModified,
}) => {
  const { t } = useTranslation("");

  const [percentage, setPercentage] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [categories, setCategories] = useState<Record<string, any>[]>([]);

  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [validations, setValidations] = useState<Record<string, string>>({});

  const storeDiscount = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setBtnLoading(true);

    const formData = new FormData();
    formData.append("percentage", percentage);
    formData.append("category_id", categoryId);
    formData.append(
      "start_date",
      startDate ? moment(startDate).format("YYYY-MM-DD") : ""
    );
    formData.append(
      "end_date",
      endDate ? moment(endDate).format("YYYY-MM-DD") : ""
    );

    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.discountStore,
        "POST",
        API_HEADERS.authenticated,
        formData
      );

      FLASH_MESSAGES.successMsg(response.success);
      setModified(true);
      onClose();
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const getCategories = async (): Promise<void> => {
    let request: any = API_PATHS.discountCateogriesList;
    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );
      setCategories(response.childrenCategories);
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      onClose();
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  const onClose = () => {
    setShowModal(false);
  };

  return (
    <Modal isOpen={onShowModal} toggle={() => onClose()} size="md" centered>
      <ModalHeader
        className="text-dark font-size-15"
        toggle={() => onClose()}
        tag="span"
      >
        {t("store_discount_title")}
      </ModalHeader>
      <form onSubmit={(e) => storeDiscount(e)}>
        <ModalBody>
          <div className="row">
            <div className="form-block col-12 col-md-6 col-sm-6">
              <label className="required">
                {t("store_discount_percentage")}
              </label>
          
                <input
                  type="number"
                  name="percentage"
                  className={
                    "form-input percentage-input" +
                    (validations.percentage ? " input-error" : "")
                  }
                  onChange={(e) => setPercentage(e.target.value)}
                  value={percentage}
                  // style={{
                  //   width: "60px",
                  //   borderTopRightRadius: "4px",
                  //   borderBottomRightRadius: "4px",
                  // }}
                  // min="0"
                  // max="100"
                  // step="0.01"
                  //   onWheel={(e) => e.currentTarget.blur()}
                />
                <span
                  className={
                    "input-group-addon" +
                    (validations.percentage ? " input-error" : "")
                  }
                >
                  %
                </span>
             
              {validations.percentage && (
                <ValidationMessage message={validations.percentage[0]} />
              )}
            </div>

            <div className="form-block col-12 col-md-6 col-sm-6">
              <label className="required">{t("discount_start_date")}</label>
              <DatePicker
                name="birthday"
                selected={startDate}
                showMonthDropdown
                showYearDropdown
                placeholderText={t("discount_start_date")}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd-MM-yyyy"
                isClearable
                className={
                  "form-datepicker" +
                  (validations.start_date ? " input-error" : "")
                }
                locale={LanguageDate.datepicker}
                //  popperClassName="navigations"
                autoComplete="off"
              />
              {validations.start_date && (
                <ValidationMessage message={validations.start_date[0]} />
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-block col-12 col-md-6 col-sm-6">
              <label className="required">{t("discount_end_date")}</label>
              <DatePicker
                name="birthday"
                selected={endDate}
                showMonthDropdown
                showYearDropdown
                placeholderText={t("discount_end_date")}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd-MM-yyyy"
                isClearable
                className={
                  "form-datepicker" +
                  (validations.end_date ? " input-error" : "")
                }
                locale={LanguageDate.datepicker}
                // popperClassName="navigations"
                autoComplete="off"
              />
              {validations.end_date && (
                <ValidationMessage message={validations.end_date[0]} />
              )}
            </div>

            <div className="form-block col-12 col-md-6 col-sm-6">
              <label className="required">{t("discount_cateogry_id")}</label>
              <Select
                placeholder={t("discount_cateogry_id")}
                className={
                  "form-react-select" +
                  (validations.category_id ? " input-error" : "")
                }
                classNamePrefix="select_input"
                name="specification"
                onChange={(e) =>
                  e ? setCategoryId(e.value) : setCategoryId("")
                }
                isClearable={true}
                isSearchable={false}
                options={categories.map((item) => ({
                  value: item.id,
                  label: item.title,
                }))}
              />
              {validations.category_id && (
                <ValidationMessage message={validations.category_id[0]} />
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="button-white"
            type="button"
            onClick={() => onClose()}
          >
            {t("discount_close")}
          </button>
          {btnLoading ? (
            <button className="button-blue has-icon" type="button">
              <i className="icon spinner-icon" />
              {t("discount_saving")}
            </button>
          ) : (
            <button className="button-blue has-icon" type="submit">
              <i className="icon add-icon" />
              {t("discount_save")}
            </button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default StoreDiscount;
